import Link from 'next/link';
import emojiFlags from 'emoji-flags';
import { nameFromCC, slugFromCC } from 'tf-post-parser';
import { fraunces } from '../Landing/fonts';
import FutureImage from '../FutureImage';
import imageCrop from 'helpers/imageCrop';

function flagSrc(countryCode) {
  if (!countryCode || !emojiFlags.countryCode(countryCode.toUpperCase()))
    return null;
  return `/flags/${countryCode.toLowerCase()}.svg`;
}

// A destination as a postcard on the rack: photo in a white mat, Fraunces
// name, mono region line. Featured cards get the passport stamp.
export default function DestinationItem({
  destination,
  isPrio,
  showDescription,
}) {
  const {
    title,
    countryCode,
    description,
    image,
    blur,
    subdivision,
    city,
    regions,
  } = destination;
  const flag = flagSrc(countryCode);
  const name = title || nameFromCC(countryCode);
  const monoLine = subdivision
    ? nameFromCC(countryCode)
    : regions && regions.length > 0
    ? regions[0]
    : null;

  return (
    <Link
      href={`/destinations/${slugFromCC(countryCode)}${
        subdivision ? `/${subdivision}` : ''
      }${city ? `/${city}` : ''}`}
      prefetch={false}
      className="tw-group tw-relative tw-block tw-rounded-2xl tw-border tw-border-solid tw-border-[#E7E0CF] dark:tw-border-[#26382F] tw-bg-white dark:tw-bg-[#15231E] tw-p-2.5 tw-pb-4 tw-no-underline tw-shadow-[0_18px_38px_-26px_rgba(29,45,40,0.5)] tw-transition-all tw-duration-300 hover:-tw-translate-y-1 hover:tw-shadow-[0_30px_55px_-26px_rgba(29,45,40,0.55)]"
    >
      <div className="tw-relative tw-aspect-[4/3] tw-overflow-hidden tw-rounded-xl">
        <FutureImage
          unoptimized
          alt={name}
          src={imageCrop(image, 800)}
          blurDataURL={blur}
          placeholder={blur ? 'blur' : undefined}
          fill
          sizes="(max-width: 576px) 96vw, (max-width: 992px) 48vw, 320px"
          priority={isPrio}
          className="tw-object-cover tw-object-center tw-transition-transform tw-duration-500 group-hover:tw-scale-[1.05]"
        />
        {flag && (
          <span className="tw-absolute tw-left-2 tw-top-2 tw-flex tw-items-center tw-rounded-full tw-bg-white/85 tw-px-1.5 tw-py-0.5 tw-shadow-sm tw-backdrop-blur-sm">
            <img
              src={flag}
              alt=""
              aria-hidden="true"
              width={13}
              height={13}
              loading={isPrio ? 'eager' : 'lazy'}
              className="tw-h-[13px] tw-w-[13px]"
            />
          </span>
        )}
      </div>
      <div className="tw-px-1.5 tw-pt-3">
        <p
          className={`${fraunces.className} tw-m-0 tw-text-lg tw-font-semibold tw-tracking-tight tw-text-[#1D2D28] dark:tw-text-[#EBE6D9] group-hover:tw-text-primary-700 dark:group-hover:tw-text-primary-300`}
        >
          {name}
        </p>
        {monoLine && (
          <p className="tw-m-0 tw-mt-1 tw-font-mono tw-text-[10px] tw-font-medium tw-uppercase tw-tracking-[0.2em] tw-text-[#8A958F]">
            {monoLine}
          </p>
        )}
        {showDescription && description && (
          <p className="tw-m-0 tw-mt-1.5 tw-overflow-hidden tw-text-[13px] tw-leading-relaxed tw-text-[#5F6F69] dark:tw-text-[#93A69E] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] [display:-webkit-box]">
            {description}
          </p>
        )}
      </div>
    </Link>
  );
}
